import React, { Suspense, useCallback, useEffect, useRef, useState } from 'react';
import { LOCAL_STORAGE_KEY } from 'constants/localStorage';
import { isStipeProvider } from 'constants/provider';
import { TRACKING_NAME } from 'constants/tracking';
import { useAnalytics } from 'hooks/useAnalytics';
import { useCheckVersion } from 'hooks/useCheckVersion';
import useClickjackingDefence from 'hooks/useClickjackingDefence';
import { useMarketingData } from 'hooks/useMarketingData';
import useModalState from 'hooks/useModalState';
import useSSEConnection from 'hooks/useSSEConnection';
import { useUrlAuthParams } from 'hooks/useUrlAuthParams';
import ls from 'local-storage';
import PaidInfoModal from 'modals/PaidInfoModal';
import TrialOfferModal from 'modals/TrialOfferModal';
import moment from 'moment';
import { useDispatch, useMappedState } from 'redux-react-hook';
import { useGetFeatureFlagsQuery } from 'services/api/featureFlagsApi';
import { useGetTokenCostsQuery } from 'services/api/tokenApi';
import { initializeAuthorization } from 'store/DEPRECATEDusers/asyncAction';
import styled from 'styled-components';
import { AlertProvider } from 'ui-kit/Alert/index';
import { FullScreenSpinner } from 'ui-kit/FullScreenSpinner';
import { isBlank } from 'utils/storeUtils';
import { stringToBoolean } from 'utils/stringToBoolean';

import TrackingAPI from '../../api/TrackingAPI';
import StripeOfferBannerModal from '../../modals/StripeOfferBannerModal';
import { formatDateToFullFormatWithSeconds, FULL_DATE_FORMAT_WITH_SECONDS, now } from '../../utils/dates';
import MetaManager from '../common/MetaManager';
import AdultWebsiteModal from '../modals/AdultWebsiteModal';
import GuestRouter from './Routers/GuestRouter';

const LazyUserRouter = React.lazy(() => import('./Routers/UserRouter'));
const LazyAdminRouter = React.lazy(() => import('./Routers/AdminRouter'));

const AVAILABLE_PAID_TYPES = ['token', 'premium'];
const isServer = typeof window === 'undefined';

const RouterApp = () => {
  const mapState = useCallback(
    state => ({
      initialized: state.users.initialized,
      currentUser:
        state.users.currentUserId == undefined
          ? null
          : state.users.usersMap.get(state.users.currentUserId)
    }),
    []
  );

  const [paidInfoState, setPaidInfoState] = useState({
    isVisible: false,
    paidType: null
  });

  const [isOpenStripeBannerModal, openStripeBannerModal, closeStripeBannerModal] = useModalState(false);
  const [isTrialModalOpen, openTrialModal, closeTrialModal] = useModalState(false);
  useGetTokenCostsQuery();
  useGetFeatureFlagsQuery();

  const { initialized, currentUser } = useMappedState(mapState);
  const dispatch = useDispatch();

  const paymentProvider = currentUser?.subscription?.paymentProvider ?? '';
  const isStripeProvider = isStipeProvider(paymentProvider);

  const subscriptionEndDate = formatDateToFullFormatWithSeconds(currentUser?.subscription?.currentPeriodEndDate ?? '');

  const expireSubscriptionDate = moment(subscriptionEndDate, FULL_DATE_FORMAT_WITH_SECONDS);
  const nowTime = now();
  const duration = moment.duration(expireSubscriptionDate.diff(nowTime));

  const daysToExpireSubscription = Math.floor(duration.asDays());
  const isExpireLessWeekSub = daysToExpireSubscription <= 6;
  const isExpiredSub = nowTime.isAfter(expireSubscriptionDate);

  const needToShowStripeOfferModal = isStripeProvider && (isExpireLessWeekSub || isExpiredSub) && currentUser?.paymentProvider !== 'stripe4';

  const expiredSubDateLsKey = !isServer ? ls.get('isExpiredSubDate') : null;
  const expiredSubDatePlusThreeDays = expiredSubDateLsKey ? moment(expiredSubDateLsKey, FULL_DATE_FORMAT_WITH_SECONDS).add(3, 'days').format('YYYY-MM-DDTHH:mm:ss.SSSZ') : null;

  const expiredSubLsKeyDateIsExpired = expiredSubDatePlusThreeDays ? nowTime.isAfter(moment(expiredSubDatePlusThreeDays)) : false;

  const isVisiblePaidModal = paidInfoState.isVisible &&
    paidInfoState.paidType &&
    AVAILABLE_PAID_TYPES.includes(paidInfoState.paidType);

  useSSEConnection(currentUser);

  const initializeAuthorizationHandler = dispatch => {
    dispatch(initializeAuthorization())
      .then(payload => {
        if (payload?.user !== undefined && !isServer) {
          window.currentUserId = payload.user.id;
          ls('tried_to_register', true);
        }

        try {
          if (!isServer) {
            const lastViewTimestamp = ls('last_view_timestamp');
            if (
              lastViewTimestamp === undefined ||
              +lastViewTimestamp < Date.now() - 24 * 60 * 60 * 1000
            ) {
              ls('last_view_timestamp', +new Date());
              TrackingAPI.trackEvent('website_view');
            }
          }
        } catch (error) {
          throw error;
        }
      })
      .catch(error => {
        throw error;
      });
  };

  const handleClosePaidInfoModal = () => {
    if (!isServer) {
      const url = new URL(window.location.href);
      url.searchParams.delete('paid');
      url.searchParams.delete('paidType');
      setPaidInfoState({
        isVisible: false,
        paidType: null
      });
      window.history.replaceState({}, '', url);
    }
  };

  useEffect(() => {
    if (!isServer) {
      const searchParams = new URLSearchParams(window.location.search);
      const paid = searchParams.get('paid');
      const paidType = searchParams.get('paidType');

      if (paid && paidType && AVAILABLE_PAID_TYPES.includes(paidType)) {
        setPaidInfoState({
          isVisible: stringToBoolean(paid),
          paidType
        });
      }
    }
  }, [isServer ? null : window.location.search]);

  useEffect(() => {
    if (!isServer) {
      const params = new URLSearchParams(window.location.search);
      if (params.get('trial') === 'true') {
        const trialExpirationDate = moment().add(3, 'days').format();
        ls.set(LOCAL_STORAGE_KEY.TRIAL_STORAGE_KEY, trialExpirationDate);
      }

      const storedTrialDate = ls.get(LOCAL_STORAGE_KEY.TRIAL_STORAGE_KEY);
      if (storedTrialDate && moment().isBefore(moment(storedTrialDate))) {
        openTrialModal();
      }
    }
  }, []);

  useEffect(() => {
    if (!isServer) {
      if (needToShowStripeOfferModal) {
        openStripeBannerModal();
      }
      const dateForEventCountDown = formatDateToFullFormatWithSeconds(nowTime);

      if (isStripeProvider && isExpiredSub) {
        if (expiredSubDateLsKey && expiredSubLsKeyDateIsExpired) {
          ls.set('isExpiredSubDate', dateForEventCountDown);
        }
        if (!expiredSubDateLsKey) {
          ls.set('isExpiredSubDate', dateForEventCountDown);
        }
      }
    }
  }, [needToShowStripeOfferModal, expiredSubDateLsKey]);

  const shouldShowTrialModal =
    isTrialModalOpen &&
    currentUser?.subscription?.paymentProvider !== 'trustpay' &&
    !needToShowStripeOfferModal;

  useCheckVersion();
  useClickjackingDefence();

  const isFirstRender = useRef(false);

  useEffect(() => {
    if (!isFirstRender.current) {
      const analyticsData = {
        current_url: window.location.pathname
      };
      TrackingAPI.trackEvent(TRACKING_NAME.PAGE_OPENING, analyticsData);
      isFirstRender.current = true;
    }
  });

  useEffect(() => {
    initializeAuthorizationHandler(dispatch);

    if (!isServer) {
      window.addEventListener('storage', event => {
        if (event.key === LOCAL_STORAGE_KEY.PAYMENT_SUCCESS_ORDER_ID) {
          initializeAuthorizationHandler(dispatch);
        }
      });
    }
  }, []);

  useUrlAuthParams(isServer);

  let router = <GuestRouter />;
  if (!!initialized && !isBlank(currentUser)) {
    router = (
      <Suspense fallback={<FullScreenSpinner />}>
        <LazyUserRouter />
      </Suspense>
    );
    if (currentUser.userRole === 'admin') {
      router = (
        <Suspense fallback={<FullScreenSpinner />}>
          <LazyAdminRouter />
        </Suspense>
      );
    }
  }

  if (!initialized && !isServer) {
    return <FullScreenSpinner />;
  }

  return (
    <>
      <MetaManager />
      <TrackingWrapper />
      <AlertProvider />
      <Wrapper>
        {router}
        {isOpenStripeBannerModal && <StripeOfferBannerModal isOpen={isOpenStripeBannerModal} onClose={closeStripeBannerModal} />}
        {shouldShowTrialModal && <TrialOfferModal isOpen={shouldShowTrialModal} onClose={closeTrialModal} />}
        {isVisiblePaidModal && (
          <PaidInfoModal
            isVisible
            onClose={handleClosePaidInfoModal}
            paidType={paidInfoState.paidType}
          />
        )}
        <AdultWebsiteModal />
      </Wrapper>
    </>
  );
};

const TrackingWrapper = () => {
  useMarketingData();
  useAnalytics();
  return null;
};

const Wrapper = styled.div`
  min-height: 100%;
`;

export default RouterApp;
